// Only explicitly selected read requests may recover from transient transport failures.
// One transport deadline bounds all attempts, independent of the bounded event queue.
const requestTimeoutMillis = 15_000;
const withDeadline = (signal?: AbortSignal | null, timeoutMillis = requestTimeoutMillis) => AbortSignal.any([
  AbortSignal.timeout(timeoutMillis), ...(signal ? [signal] : []),
]);
export async function fetchWithReadRetry(url: string, init: RequestInit, retryRead: boolean, retryEventConflict = false): Promise<Response> {
  // Event reads fence shared authority records on the server. Avoid making one tab's
  // panels contend with each other; this schedules transport only, never authorization.
  const release = retryEventConflict && (init.method || "GET") === "GET" ? await acquireEventRead(withDeadline(init.signal)) : undefined;
  // A slow preceding panel must not consume this request's entire network budget.
  // Caller/scope cancellation still applies during both phases. All retries share
  // this one deadline; writes get the same 15 seconds and are never queued/replayed.
  // Immutable round-video completion can include bounded metadata and thumbnail decoding.
  // Only this exact POST gets the longer budget; it is never automatically replayed.
  const completion = init.method === "POST" && /^\/api\/admin\/events\/eventRound\/mediaUpload\/complete(?:\?|$)/.test(url);
  try { return await fetchAttempts(url, { ...init, signal: withDeadline(init.signal, completion ? 120_000 : requestTimeoutMillis) }, retryRead, retryEventConflict); }
  finally { release?.(); }
}

let eventReadActive = false;
const eventReadWaiters: Array<() => void> = [];
function acquireEventRead(signal?: AbortSignal | null): Promise<() => void> {
  signal?.throwIfAborted();
  return new Promise((resolve, reject) => {
    const grant = () => {
      signal?.removeEventListener("abort", cancel);
      resolve(() => {
        const next = eventReadWaiters.shift();
        if (next) next(); else eventReadActive = false;
      });
    };
    const cancel = () => {
      const index = eventReadWaiters.indexOf(grant);
      if (index >= 0) eventReadWaiters.splice(index, 1);
      signal?.removeEventListener("abort", cancel);
      reject(signal?.reason);
    };
    if (!eventReadActive) { eventReadActive = true; grant(); }
    else { eventReadWaiters.push(grant); signal?.addEventListener("abort", cancel, { once: true }); }
  });
}

async function fetchAttempts(url: string, init: RequestInit, retryRead: boolean, retryEventConflict: boolean): Promise<Response> {
  retryRead = retryRead && (init.method || "GET") === "GET";
  retryEventConflict = retryEventConflict && (init.method || "GET") === "GET";
  // One last settling read also covers the parent workspace after close/seal, not
  // only its analytics panel. All attempts retain the same transport deadline.
  const delays = retryEventConflict ? [500, 1500, 500] : [500, 1500];
  for (let attempt = 0; ; attempt++) {
    init.signal?.throwIfAborted();
    try {
      const response = await fetch(url, init);
      if (attempt >= delays.length) return response;
      let eventConflict = false;
      if (retryEventConflict && [400, 409].includes(response.status)) {
        const payload = await response.clone().json().catch(() => null);
        eventConflict = payload?.success === false && payload?.errorCode === "EVENT_UNIT_CONCURRENT_CHANGE";
      }
      if (!eventConflict && !(retryRead && [502, 503, 504].includes(response.status))) return response;
      await response.body?.cancel();
    } catch (error) {
      if (!retryRead || attempt >= delays.length || !(error instanceof TypeError)) throw error;
    }
    // Spread competing panels across the backoff window instead of retrying in lockstep.
    await waitForRetry(delays[attempt] + (retryEventConflict ? Math.floor(Math.random() * 500) : 0), init.signal);
  }
}

function waitForRetry(delay: number, signal?: AbortSignal | null): Promise<void> {
  signal?.throwIfAborted();
  return new Promise((resolve, reject) => {
    const cancel = () => { clearTimeout(timer); signal?.removeEventListener("abort", cancel); reject(signal?.reason); };
    const timer = setTimeout(() => { signal?.removeEventListener("abort", cancel); resolve(); }, delay);
    signal?.addEventListener("abort", cancel, { once: true });
  });
}
